import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/**
 * localStorage flag set when a password-reset email is requested from this
 * browser. AuthCallback reads it when the reset link lands, so the returning
 * user is sent to /auth/reset (choose a new password) instead of /app.
 * localStorage rather than sessionStorage because the email link opens in a
 * fresh tab.
 */
export const PENDING_RECOVERY_KEY = 'haphe.pending_recovery';

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  initialized: boolean;
  init: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  /**
   * Creates an email + password account. With "confirm email" on in Supabase
   * (the default) no session comes back until the link in the email is
   * opened, and `needsConfirmation` is true.
   */
  signUpWithPassword: (args: {
    email: string;
    password: string;
    fullName: string;
  }) => Promise<{ error: string | null; needsConfirmation: boolean }>;
  sendPasswordReset: (email: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  loading: false,
  initialized: false,

  init: async () => {
    if (get().initialized) return;
    set({ loading: true });
    const { data } = await supabase.auth.getSession();
    set({
      session: data.session,
      user: data.session?.user ?? null,
      loading: false,
      initialized: true,
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      set({ session, user: session?.user ?? null });
    });
  },

  signInWithGoogle: async () => {
    set({ loading: true });
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    // Note: on success the browser navigates to Google — we won't reach
    // the next line in the happy path. Only resets loading on error.
    if (error) set({ loading: false });
    return { error: error?.message ?? null };
  },

  signInWithPassword: async (email, password) => {
    set({ loading: true });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    set({
      loading: false,
      ...(data.session ? { session: data.session, user: data.session.user } : {}),
    });
    return { error: error ? friendlyAuthError(error.message) : null };
  },

  signUpWithPassword: async ({ email, password, fullName }) => {
    set({ loading: true });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Lands in user_metadata.full_name — the same place Google puts it,
        // which NewCardWizard / Template read for the first card's name.
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    set({ loading: false });
    if (error) return { error: friendlyAuthError(error.message), needsConfirmation: false };
    // With confirmation on, Supabase answers an already-registered address
    // with a placeholder user that has no identities (anti-enumeration).
    // Saying "check your email" there would strand the person, so be plain.
    if (data.user && data.user.identities?.length === 0) {
      return { error: 'An account with this email already exists. Sign in instead.', needsConfirmation: false };
    }
    if (data.session) {
      set({ session: data.session, user: data.session.user });
      return { error: null, needsConfirmation: false };
    }
    return { error: null, needsConfirmation: true };
  },

  sendPasswordReset: async (email) => {
    set({ loading: true });
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
    set({ loading: false });
    if (!error) {
      try {
        localStorage.setItem(PENDING_RECOVERY_KEY, '1');
      } catch {
        /* storage blocked — the callback still falls back to /app */
      }
    }
    return { error: error ? friendlyAuthError(error.message) : null };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null });
  },
}));

/** Supabase's auth errors are terse; translate the common ones. */
export function friendlyAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials')) return 'Wrong email or password.';
  if (m.includes('email not confirmed')) return 'Confirm your email first — the link is in your inbox.';
  if (m.includes('already registered')) return 'An account with this email already exists. Sign in instead.';
  if (m.includes('password should be') || m.includes('weak password')) {
    return 'Choose a stronger password — at least 8 characters.';
  }
  if (m.includes('rate limit') || m.includes('too many')) return 'Too many attempts. Wait a minute and try again.';
  if (m.includes('signups not allowed') || m.includes('email signups are disabled')) {
    return 'Email sign-up is switched off right now. Continue with Google instead.';
  }
  return message;
}
